import React from 'react';
import {Box, Grid, styled, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Player, Profile} from '../../../interfaces';
import {DateTime} from 'luxon';
import ProfileIdentity from '../../shared/ProfileIdentity';
import {useAppSelector} from '../../../store/hooks';
import {
  getHeightLabel,
  getWeightLabel,
} from '../../../services/measurementService';

const ProfileIdentityWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginLeft: 16,
  marginRight: 16,
}));

const ProfileStatGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

interface Props {
  profileDetail: Profile;
  player?: Player | null;
}

const PlayerProfileRow: React.FC<Props> = ({profileDetail, player}) => {
  const {
    firstName,
    lastName,
    position,
    profilePictureFileKey,
    shot,
    height,
    weight,
    hometown,
    dateOfBirth,
  } = profileDetail;
  const number = player?.number;
  const {t} = useTranslation();
  const preferredUnits = useAppSelector(
    state => state.profile.profile?.preferredUnits,
  );
  const isMetric = preferredUnits === 'metric';
  const heightLabel = getHeightLabel(height, isMetric);
  const weightLabel = getWeightLabel(weight, isMetric);
  const birthday =
    dateOfBirth &&
    DateTime.fromJSDate(new Date(dateOfBirth)).toFormat('MM/dd/yyyy');
  const getAge = () => {
    if (!dateOfBirth) {
      return t('general.notAvailable');
    }
    const dateOfBirthDateObject = DateTime.fromJSDate(new Date(dateOfBirth));
    const now = DateTime.now();
    const age = now.diff(dateOfBirthDateObject, 'years').years;
    return Math.floor(age);
  };

  return (
    <ProfileIdentityWrapper>
      <Grid container sx={{justifyContent: 'space-between'}}>
        <Grid item xs={12} sx={{marginBottom: 2}}>
          <ProfileIdentity
            firstName={firstName}
            lastName={lastName}
            // position={position}
            number={number}
            profilePictureUrl={profilePictureFileKey}
          />
        </Grid>
        <ProfileStatGrid item xs={6} lg={2} sx={{mb: 2}}>
          <Typography variant="h5">
            {t('profile.position')}:{' '}
            {position ? t(`position.${position}`) : t('general.notAvailable')}
          </Typography>
          <Typography sx={{textTransform: 'capitalize'}} variant="h5">
            {t('profile.shot')}: {shot || t('general.notAvailable')}
          </Typography>
        </ProfileStatGrid>
        <ProfileStatGrid item xs={6} lg={2} sx={{mb: 2}}>
          <Typography variant="h5">
            {t('profile.height')}: {heightLabel ?? t('general.notAvailable')}
          </Typography>
          <Typography variant="h5">
            {t('profile.weight')}: {weightLabel ?? t('general.notAvailable')}
          </Typography>
        </ProfileStatGrid>
        <ProfileStatGrid item xs={6} lg={2} sx={{mb: 2}}>
          <Typography variant="h5">
            {t('profile.birthday')}: {birthday || t('general.notAvailable')}
          </Typography>
          <Typography variant="h5">
            {t('profile.age')}: {getAge()}
          </Typography>
        </ProfileStatGrid>
        <ProfileStatGrid item xs={6} lg={2} sx={{mb: 2}}>
          <Typography variant="h5">
            {t('profile.hometown')}: {hometown || t('general.notAvailable')}
          </Typography>
        </ProfileStatGrid>
      </Grid>
    </ProfileIdentityWrapper>
  );
};

export default PlayerProfileRow;
